import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Job, JobList } from '../interfaces/job-list';
import { JobFilterParams, JobListService } from '../services/job-list.service';
import { environment } from 'src/environments/environment';

const COMPANY_COLORS = [
  '#e8f0ff',
  '#fff0f5',
  '#f0f4ff',
  '#fff8ed',
  '#f0fdf4',
  '#fdf4ff',
  '#fef9f0',
  '#f5f5f5',
];

function getColor(index: number): string {
  return COMPANY_COLORS[index % COMPANY_COLORS.length];
}

function timeAgo(dateStr: string): { label: string; daysAgo: number } {
  const created = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffMins < 60) return { label: `${diffMins} min ago`, daysAgo: 0 };
  if (diffHrs < 24) return { label: `${diffHrs}h ago`, daysAgo: 0 };
  if (diffDays === 1) return { label: '1 day ago', daysAgo: 1 };
  return { label: `${diffDays} days ago`, daysAgo: diffDays };
}

function mapApiJob(apiJob: JobList, index: number): Job {
  const { label, daysAgo } = timeAgo(apiJob.createdAt);
  const isConfidential = !!apiJob.confidential;
  return {
    id: apiJob.id,
    title: apiJob.jobPostTitle ?? apiJob.position?.title ?? 'Unknown Position',
    jobPostTitle: apiJob.jobPostTitle,
    positionTitle: apiJob.positionTitle ?? apiJob.position?.title,
    company: isConfidential ? 'Confidential Company' : (apiJob.companyName ?? 'Unknown Company'),
    companyId: isConfidential ? '' : apiJob.companyId,
    companyColor: getColor(index),
    companyLogoUrl: !isConfidential && apiJob.companyLogoUrl
      ? `${environment.apiImagePath}${apiJob.companyLogoUrl.replace(/\\/g, '/')}`
      : '',
    companyInitial: isConfidential
      ? 'C'
      : (apiJob.companyName ?? 'C').charAt(0).toUpperCase(),
    type: apiJob.jobType ?? 'Full-time',
    salaryMin: apiJob.hideSalary ? null : (apiJob.minSalary ?? null),
    salaryMax: apiJob.hideSalary ? null : (apiJob.maxSalary ?? null),
    hideSalary: apiJob.hideSalary ?? false,
    location:
      apiJob.city && apiJob.country
        ? `${apiJob.city}, ${apiJob.country}`
        : (apiJob.city ?? apiJob.country ?? 'N/A'),
    workplace: apiJob.workplace ?? '',
    postedAgo: label,
    postedDaysAgo: daysAgo,
    minExperience: apiJob.minExperience ?? null,
    maxExperience: apiJob.maxExperience ?? null,
    description: apiJob.jobDescription ?? 'No description provided.',
    skills: apiJob.skillsAndTools ?? [],
    requirements: apiJob.requirements ?? [],
    benefits: apiJob.benefits ?? [],
    responsibilities: apiJob.responsibilities ?? [],
    isNew: daysAgo === 0,
    jobLevel: apiJob.jobLevel ?? apiJob.position?.jobLevel ?? '',
    department: apiJob.position?.department ?? '',
    categories: apiJob.categories ?? [], // ← add this line
    currency: apiJob.currency,
    confidential: isConfidential,
  };
}

@Component({
  selector: 'app-uesr-job-list',
  templateUrl: './uesr-job-list.component.html',
  styleUrls: ['./uesr-job-list.component.css'],
})
export class UesrJobListComponent implements OnInit {
  @ViewChild('jobsSection') jobsSection!: ElementRef<HTMLElement>;

  constructor(
    private router: Router,
    private jobListService: JobListService,
  ) {}

  // ── State ────────────────────────────────────────────────────────
  jobs: Job[] = [];
  isLoading = true;
  hasError = false;
  filtersOpen = false;

  // ── Filter state ─────────────────────────────────────────────────
  activeSalaryTab: 'Hourly' | 'Monthly' | 'Yearly' = 'Yearly';
  selectedSalaryRanges: string[] = [];
  selectedDatePosting: string = '';
  selectedExperience: string = '';
  selectedEmploymentTypes: string[] = [];
  selectedPostType: string = '';
  selectedWorkplace: string = '';

  // ── Static option lists ──────────────────────────────────────────
  salaryOptions = {
    Hourly: ['<$15/hr', '$15–$25/hr', '$25–$50/hr', '$50+/hr'],
    Monthly: ['<$2k/mo', '$2k–$4k/mo', '$4k–$7k/mo', '$7k+/mo'],
    Yearly: ['<$30K', '$30K–$50K', '$50K–$80K', '$80K+'],
  };

  /** Maps directly to the API's postingDate param values */
  dateOptions = [
    { label: 'All time', value: 'ALL_TIME' },
    { label: 'Last 24 hours', value: 'LAST_24_HOURS' },
    { label: 'Last 3 days', value: 'LAST_3_DAYS' },
    { label: 'Last 7 days', value: 'LAST_7_DAYS' },
  ];

  /**
   * Year-range buckets → sent as minExperience / maxExperience.
   * `min: null` means 0  (no lower bound passed).
   * `max: null` means unlimited (no upper bound passed).
   */
  experienceOptions: {
    label: string;
    value: string;
    min: number | null;
    max: number | null;
  }[] = [
    { label: 'Any experience', value: '', min: null, max: null },
    { label: '0–1 year', value: '0-1', min: 0, max: 1 },
    { label: '1–3 years', value: '1-3', min: 1, max: 3 },
    { label: '3–5 years', value: '3-5', min: 3, max: 5 },
    { label: '5–10 years', value: '5-10', min: 5, max: 10 },
    { label: '10+ years', value: '10+', min: 10, max: null },
  ];

  /** Matches the API's jobTypes param values exactly */
  employmentOptions = ['Full-time', 'Part-time'];

  /** Maps to API's postTypes param: '' = no filter, else 'JOB' or 'INTERNSHIP' */
  postTypeOptions = [
    { label: 'All posts', value: '' },
    { label: 'Jobs', value: 'JOB' },
    { label: 'Internships', value: 'INTERNSHIP' },
  ];

  /**
   * Workplace filter → sent as API location param.
   * REMOTE_JOB → workplace = REMOTE
   * NEAR_ME    → on-site / near user
   */
  workplaceOptions = [
    { label: 'All', value: '' },
    { label: 'Remote', value: 'REMOTE_JOB' },
    { label: 'Near Me ', value: 'NEAR_ME' },
    { label: 'Exact Location', value: 'EXACT_LOCATION' },
  ];

  // ── Salary conversion maps ───────────────────────────────────────
  private readonly salaryTypeMap: Record<
    string,
    'PER_HOUR' | 'PER_MONTH' | 'PER_YEAR'
  > = {
    Hourly: 'PER_HOUR',
    Monthly: 'PER_MONTH',
    Yearly: 'PER_YEAR',
  };

  private readonly salaryRangeValues: Record<
    string,
    { min: number | null; max: number | null }
  > = {
    // Hourly
    '<$15/hr': { min: null, max: 15 },
    '$15–$25/hr': { min: 15, max: 25 },
    '$25–$50/hr': { min: 25, max: 50 },
    '$50+/hr': { min: 50, max: null },
    // Monthly
    '<$2k/mo': { min: null, max: 2000 },
    '$2k–$4k/mo': { min: 2000, max: 4000 },
    '$4k–$7k/mo': { min: 4000, max: 7000 },
    '$7k+/mo': { min: 7000, max: null },
    // Yearly
    '<$30K': { min: null, max: 30000 },
    '$30K–$50K': { min: 30000, max: 50000 },
    '$50K–$80K': { min: 50000, max: 80000 },
    '$80K+': { min: 80000, max: null },
  };

  // ── Lifecycle ────────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadJobs();
  }

  // ── Navigation ───────────────────────────────────────────────────
  navigateToJob(jobId: string): void {
    this.router.navigate(['/job-description', jobId]);
  }

  // ── Mobile sheet ─────────────────────────────────────────────────
  openFilters(): void {
    this.filtersOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeFilters(): void {
    this.filtersOpen = false;
    document.body.style.overflow = '';
  }

  // ── Computed ─────────────────────────────────────────────────────
  get filteredJobs(): Job[] {
    return this.jobs;
  }

  get activeFilterCount(): number {
    let n = 0;
    if (this.selectedSalaryRanges.length) n++;
    if (this.selectedDatePosting) n++;
    if (this.selectedExperience) n++;
    if (this.selectedEmploymentTypes.length) n++;
    if (this.selectedPostType) n++;
    if (this.selectedWorkplace) n++;
    return n;
  }

  get currentSalaryOptions(): string[] {
    return this.salaryOptions[this.activeSalaryTab];
  }

  isSalarySelected(range: string): boolean {
    return this.selectedSalaryRanges.includes(range);
  }
  isEmploymentSelected(type: string): boolean {
    return this.selectedEmploymentTypes.includes(type);
  }

  // ── Formatters ───────────────────────────────────────────────────
  formatSalary(val: number | null, currency: string): string {
    if (val === null) return '';
    return val >= 1000
      ? `${(val / 1000).toFixed(0)}K ${currency}`
      : `${val} ${currency}`;
  }

  getWorkplaceLabel(workplace: string): string {
    const map: Record<string, string> = {
      REMOTE: 'Remote',
      ON_SITE: 'On-site',
      HYBRID: 'Hybrid',
    };
    return map[workplace] ?? workplace;
  }

  formatJobLevel(level: string): string {
    if (!level) return '';
    return level.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  }

  // ── Build filter object for service ─────────────────────────────
  private buildFilters(): JobFilterParams {
    const filters: JobFilterParams = {};

    // Employment types → jobTypes
    if (this.selectedEmploymentTypes.length)
      filters.jobTypes = [...this.selectedEmploymentTypes];

    // Post type → postTypes
    if (this.selectedPostType) filters.postTypes = [this.selectedPostType];

    // Date of posting → postingDate (API value stored directly)
    if (this.selectedDatePosting)
      filters.postingDate = this.selectedDatePosting;

    // Workplace → location
    if (this.selectedWorkplace) filters.location = this.selectedWorkplace;

    // Work experience → minExperience / maxExperience
    if (this.selectedExperience) {
      const opt = this.experienceOptions.find(
        (e) => e.value === this.selectedExperience,
      );
      if (opt) {
        if (opt.min !== null) {
          filters.minExperience = opt.min;
        }
        if (opt.max !== null) {
          filters.maxExperience = opt.max;
        }
      }
    }

    // Salary ranges → salaryFilters array
    if (this.selectedSalaryRanges.length) {
      const salaryType = this.salaryTypeMap[this.activeSalaryTab];
      filters.salaryFilters = this.selectedSalaryRanges
        .map((r) => this.salaryRangeValues[r])
        .filter(Boolean)
        .map(({ min, max }) => ({
          minSalary: min,
          maxSalary: max,
          salaryType,
        }));
    }

    return filters;
  }

  // ── Load / reload ────────────────────────────────────────────────
  loadJobs(): void {
    this.isLoading = true;
    this.hasError = false;

    this.jobListService.getJobs(this.buildFilters()).subscribe({
      next: (apiJobs) => {
        this.jobs = (apiJobs ?? []).map((j, i) => mapApiJob(j, i));
        this.isLoading = false;
        this._scrollToJobs();
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
      },
    });
  }

  private _scrollToJobs(): void {
    setTimeout(() => {
      this.jobsSection?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  }

  // ── Filter toggle helpers ─────────────────────────────────────────
  setSalaryTab(tab: 'Hourly' | 'Monthly' | 'Yearly'): void {
    this.activeSalaryTab = tab;
    this.selectedSalaryRanges = [];
    this.loadJobs();
  }

  toggleSalaryRange(range: string): void {
    const idx = this.selectedSalaryRanges.indexOf(range);
    if (idx > -1) this.selectedSalaryRanges.splice(idx, 1);
    else this.selectedSalaryRanges.push(range);
    this.loadJobs();
  }

  toggleEmploymentType(type: string): void {
    const idx = this.selectedEmploymentTypes.indexOf(type);
    if (idx > -1) this.selectedEmploymentTypes.splice(idx, 1);
    else this.selectedEmploymentTypes.push(type);
    this.loadJobs();
  }

  clearFilters(): void {
    this.activeSalaryTab = 'Yearly';
    this.selectedSalaryRanges = [];
    this.selectedDatePosting = '';
    this.selectedExperience = '';
    this.selectedEmploymentTypes = [];
    this.selectedPostType = '';
    this.selectedWorkplace = '';
    this.loadJobs();
  }
}
